import { useCallback, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { useIntl } from "../lib/i18n";

// ImagePicker — 33.5 upload widget.
//
// Renders a Tailwind button that opens the OS file dialog; on selection
// POSTs the file to /api/v1/tenant/site/images as multipart/form-data.
// The server generates 800px + 1600px variants + keeps the original,
// then returns { imageId, variants: [{width, url, expiresAt, bytes}, …] }.
//
// The parent decides what to do with the response — SiteBuilderPage stores
// the 800px url as the display url and the 1600px url as an optional
// retina/large variant on the section block's JSONB props bag.
//
// Reusable across every place a firm image needs to be uploaded (hero,
// team photos, section illustrations, attorney portraits when we ship
// the attorney editor).

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";
const ACCEPT = "image/jpeg,image/png,image/webp";
const MAX_BYTES = 8 * 1024 * 1024;

export type UploadedVariant = {
  width: number;
  url: string;
  expiresAt: string;
  bytes: number;
};

export type UploadResult = {
  imageId: string;
  variants: UploadedVariant[];
};

type Props = {
  onSelect: (result: UploadResult) => void;
  currentUrl?: string | null; // when set, the button label becomes "replace"
  disabled?: boolean;
  ariaLabel?: string;
};

export default function ImagePicker({
  onSelect,
  currentUrl,
  disabled,
  ariaLabel,
}: Props) {
  const { session } = useAuth();
  const { t } = useIntl();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [status, setStatus] = useState<"idle" | "uploading" | "error">("idle");
  const [errKey, setErrKey] = useState<"size" | "type" | "network" | null>(null);

  const onChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setErrKey(null);
      // Client-side size guard — matches the server's imageAcceptMax.
      if (file.size > MAX_BYTES) {
        setStatus("error");
        setErrKey("size");
        return;
      }
      // Client-side MIME guard — the server rejects with 415 anyway, but
      // failing fast gives a better UX than a round-trip.
      if (!ACCEPT.split(",").includes(file.type)) {
        setStatus("error");
        setErrKey("type");
        return;
      }
      setStatus("uploading");
      try {
        const fd = new FormData();
        fd.append("file", file);
        // Bypass the api() JSON wrapper — fetch sets multipart
        // Content-Type + boundary automatically when body is FormData.
        const res = await fetch(`${BASE}/api/v1/tenant/site/images`, {
          method: "POST",
          headers: session?.token ? { Authorization: `Bearer ${session.token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          setStatus("error");
          if (res.status === 413) setErrKey("size");
          else if (res.status === 415) setErrKey("type");
          else setErrKey("network");
          return;
        }
        const body = (await res.json()) as UploadResult;
        setStatus("idle");
        onSelect(body);
      } catch (err) {
        setStatus("error");
        setErrKey("network");
        console.error("image upload failed", err);
      } finally {
        // Reset so re-picking the same file re-triggers onChange.
        if (fileRef.current) fileRef.current.value = "";
      }
    },
    [onSelect, session?.token],
  );

  const buttonLabel = currentUrl
    ? t("image_picker.replace")
    : t("image_picker.upload");

  return (
    <div className="flex items-center gap-2" data-testid="image-picker">
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        onChange={onChange}
        disabled={disabled || status === "uploading"}
        className="hidden"
        aria-label={ariaLabel ?? buttonLabel}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={disabled || status === "uploading"}
        className="rounded-md border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
      >
        {status === "uploading" ? t("image_picker.uploading") : buttonLabel}
      </button>
      {currentUrl && status === "idle" && (
        <img
          src={currentUrl}
          alt=""
          className="h-8 w-8 rounded object-cover ring-1 ring-slate-200"
        />
      )}
      {status === "error" && errKey === "size" && (
        <span className="text-xs text-rose-600">
          {t("image_picker.error.size")}
        </span>
      )}
      {status === "error" && errKey === "type" && (
        <span className="text-xs text-rose-600">
          {t("image_picker.error.type")}
        </span>
      )}
      {status === "error" && errKey === "network" && (
        <span className="text-xs text-rose-600">
          {t("image_picker.error.network")}
        </span>
      )}
    </div>
  );
}
